import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import Button from '@carbon/react/es/components/Button/Button';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import Search from '@carbon/react/es/components/Search/Search';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import Modal from '@carbon/react/es/components/Modal/Modal';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Add from '@carbon/icons-react/es/Add';
import type { CatalogPart, HardwareCategory, OrgPartVariant, CatalogSpecs } from '@ddp/shared';

import { useT } from '../../i18n/index.js';
import { useFleet } from './use-fleet.js';
import { useCatalog } from './use-catalog.js';
import { useBuild } from './use-build.js';
import { usePartVariants } from './use-part-variants.js';
import { categoryKey } from './CatalogBrowser.js';
import { KIND_LABEL } from './labels.js';

/** Une ligne de nomenclature en cours d'édition. `orgVariantId` = variante tunée retenue, ou `null`. */
interface DraftLine {
  key: string;
  catalogPartId: string;
  orgVariantId: string | null;
  quantity: number;
  slot?: string;
}

let seq = 0;
const nextKey = () => `l${(seq += 1)}`;

/**
 * Nomenclature (BOM) d'un matériel (ADR-0030 §4). On choisit un matériel de la flotte, on compose sa
 * liste de pièces du catalogue (quantité, emplacement), avec en option une **variante tunée**
 * d'organisation par ligne — toujours rattachée à l'officiel de la ligne (invariant vérifié à
 * l'écriture côté API). L'enregistrement remplace la nomenclature en bloc, de façon idempotente.
 */
export function HardwareBuild() {
  const t = useT();
  const { assets, loading: assetsLoading } = useFleet();
  const { parts } = useCatalog();
  const { variants, create: createVariant } = usePartVariants();
  const byId = useMemo(() => new Map(parts.map((p) => [p.id, p])), [parts]);
  const variantById = useMemo(() => new Map(variants.map((v) => [v.id, v])), [variants]);
  // Variantes disponibles par officiel parent, pour n'offrir sur une ligne que des variantes valides.
  const variantsByParent = useMemo(() => {
    const m = new Map<string, OrgPartVariant[]>();
    for (const v of variants) {
      const list = m.get(v.parentPartId) ?? [];
      list.push(v);
      m.set(v.parentPartId, list);
    }
    return m;
  }, [variants]);

  const [assetId, setAssetId] = useState<string | null>(null);
  useEffect(() => {
    if (!assetId && assets[0]) setAssetId(assets[0].id);
  }, [assets, assetId]);

  const { items, loading: buildLoading, save } = useBuild(assetId);
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [variantFor, setVariantFor] = useState<{ lineKey: string; part: CatalogPart } | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(
      items.map((it) => ({
        key: nextKey(),
        catalogPartId: it.catalogPartId,
        orgVariantId: it.orgVariantId,
        quantity: it.quantity,
        slot: it.slot ?? undefined,
      })),
    );
    setDirty(false);
    setSaved(false);
  }, [items]);

  const mutate = (fn: (d: DraftLine[]) => DraftLine[]) => {
    setDraft((d) => fn(d));
    setDirty(true);
    setSaved(false);
  };
  const addPart = (p: CatalogPart) => {
    setPickerOpen(false);
    mutate((d) => {
      const existing = d.find((l) => l.catalogPartId === p.id && !l.orgVariantId);
      if (existing) return d.map((l) => (l === existing ? { ...l, quantity: l.quantity + 1 } : l));
      return [
        ...d,
        { key: nextKey(), catalogPartId: p.id, orgVariantId: null, quantity: 1, slot: p.category },
      ];
    });
  };
  const setQty = (key: string, delta: number) =>
    mutate((d) =>
      d.map((l) =>
        l.key === key ? { ...l, quantity: Math.min(999, Math.max(1, l.quantity + delta)) } : l,
      ),
    );
  const removeLine = (key: string) => mutate((d) => d.filter((l) => l.key !== key));
  const setVariant = (key: string, orgVariantId: string | null) =>
    mutate((d) => d.map((l) => (l.key === key ? { ...l, orgVariantId } : l)));

  // Création d'une variante depuis une ligne : on la rattache à l'officiel de la ligne (invariant),
  // puis on la sélectionne sur cette ligne.
  const onVariantCreated = (lineKey: string, v: OrgPartVariant) => {
    setVariantFor(null);
    setVariant(lineKey, v.id);
  };

  const onSave = async () => {
    setError(null);
    try {
      await save({
        items: draft.map((l) => ({
          catalogPartId: l.catalogPartId,
          orgVariantId: l.orgVariantId,
          quantity: l.quantity,
          slot: l.slot,
        })),
      });
      setDirty(false);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('hw.build.saveError'));
    }
  };

  const totalQty = draft.reduce((s, l) => s + l.quantity, 0);
  const tunedCount = draft.filter((l) => l.orgVariantId).length;
  const covered = new Set(draft.map((l) => byId.get(l.catalogPartId)?.category).filter(Boolean));

  if (assetsLoading) return <p className="ddp-cat__muted">{t('common.loading')}</p>;
  if (assets.length === 0) {
    return (
      <div className="ddp-empty">
        <p className="ddp-empty__title">{t('hw.build.noAsset.title')}</p>
        <p className="ddp-empty__body">
          {t('hw.build.noAsset.body')} <RouterLink to="/">{t('hw.build.noAsset.link')}</RouterLink>
        </p>
      </div>
    );
  }

  return (
    <div className="ddp-build">
      <div className="ddp-build__bar">
        <Select
          id="build-asset"
          size="sm"
          labelText={t('hw.build.assetLabel')}
          hideLabel
          value={assetId ?? ''}
          onChange={(e) => setAssetId(e.target.value)}
        >
          {assets.map((a) => (
            <SelectItem key={a.id} value={a.id} text={`${a.name} — ${t(KIND_LABEL[a.kind])}`} />
          ))}
        </Select>
        <span className="ddp-build__spacer" />
        <Button size="sm" kind="tertiary" renderIcon={Add} onClick={() => setPickerOpen(true)}>
          {t('hw.build.addPart')}
        </Button>
        <Button size="sm" disabled={!dirty} onClick={() => void onSave()}>
          {t('hw.build.save')}
        </Button>
      </div>

      {saved && (
        <InlineNotification
          kind="success"
          title={t('hw.build.saved')}
          lowContrast
          hideCloseButton
        />
      )}
      {error && (
        <InlineNotification kind="error" title={t('common.error')} subtitle={error} lowContrast />
      )}

      <div className="ddp-build__grid">
        <div>
          {buildLoading ? (
            <p className="ddp-cat__muted">{t('common.loading')}</p>
          ) : draft.length === 0 ? (
            <div className="ddp-empty">
              <p className="ddp-empty__body">{t('hw.build.empty')}</p>
            </div>
          ) : (
            <div className="ddp-table-scroll">
              <table className="ddp-build__table">
                <thead>
                  <tr>
                    <th>{t('hw.build.col.slot')}</th>
                    <th>{t('hw.build.col.part')}</th>
                    <th>{t('hw.build.col.qty')}</th>
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {draft.map((l) => {
                    const p = byId.get(l.catalogPartId);
                    const lineVariants = variantsByParent.get(l.catalogPartId) ?? [];
                    const tuned = l.orgVariantId ? variantById.get(l.orgVariantId) : undefined;
                    return (
                      <tr key={l.key}>
                        <td>
                          <span className="ddp-build__slot">
                            {p ? t(categoryKey(p.category)) : (l.slot ?? '—')}
                          </span>
                        </td>
                        <td>
                          <div className="ddp-build__part">
                            {p ? `${p.brand} ${p.model}` : l.catalogPartId}
                            {tuned && (
                              <span className="ddp-build__tuned">
                                {t('hw.build.tuned')} · {tuned.label}
                              </span>
                            )}
                          </div>
                          <div className="ddp-build__sub">{p?.variant || ''}</div>
                          <div className="ddp-build__ref">{l.catalogPartId}</div>
                          <div className="ddp-build__variant">
                            <Select
                              id={`var-${l.key}`}
                              size="sm"
                              labelText={t('hw.build.variantLabel')}
                              hideLabel
                              value={l.orgVariantId ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '__new__' && p)
                                  setVariantFor({ lineKey: l.key, part: p });
                                else setVariant(l.key, v || null);
                              }}
                            >
                              <SelectItem value="" text={t('hw.build.official')} />
                              {lineVariants.map((v) => (
                                <SelectItem key={v.id} value={v.id} text={v.label} />
                              ))}
                              <SelectItem value="__new__" text={t('hw.build.newVariant')} />
                            </Select>
                          </div>
                        </td>
                        <td>
                          <span className="ddp-build__qty">
                            <button
                              type="button"
                              aria-label={t('hw.build.qtyMinus')}
                              onClick={() => setQty(l.key, -1)}
                            >
                              –
                            </button>
                            <span>{l.quantity}</span>
                            <button
                              type="button"
                              aria-label={t('hw.build.qtyPlus')}
                              onClick={() => setQty(l.key, 1)}
                            >
                              +
                            </button>
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="ddp-build__rm"
                            aria-label={t('hw.build.remove')}
                            onClick={() => removeLine(l.key)}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="ddp-build__note">{t('hw.build.note')}</p>
        </div>

        <aside className="ddp-build__side">
          <h3>{t('hw.build.summary')}</h3>
          <div className="ddp-build__kv">
            <span>{t('hw.build.lines')}</span>
            <b>{draft.length}</b>
          </div>
          <div className="ddp-build__kv">
            <span>{t('hw.build.totalQty')}</span>
            <b>{totalQty}</b>
          </div>
          <div className="ddp-build__kv">
            <span>{t('hw.build.tunedCount')}</span>
            <b>{tunedCount}</b>
          </div>
          <h3 className="ddp-build__cov-title">{t('hw.build.coverage')}</h3>
          <div className="ddp-build__cov">
            {covered.size === 0 ? (
              <span className="ddp-cat__muted">—</span>
            ) : (
              [...covered].map((c) => (
                <span key={c} className="ddp-build__covtag">
                  {t(categoryKey(c as HardwareCategory))}
                </span>
              ))
            )}
          </div>
        </aside>
      </div>

      {pickerOpen && (
        <PartPicker parts={parts} onPick={addPart} onClose={() => setPickerOpen(false)} />
      )}
      {variantFor && (
        <VariantModal
          part={variantFor.part}
          onCreate={createVariant}
          onCreated={(v) => onVariantCreated(variantFor.lineKey, v)}
          onClose={() => setVariantFor(null)}
        />
      )}
    </div>
  );
}

/** Tiroir modal : pioche une pièce officielle du catalogue à ajouter à la nomenclature. */
function PartPicker({
  parts,
  onPick,
  onClose,
}: {
  parts: CatalogPart[];
  onPick: (p: CatalogPart) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = parts.filter(
    (p) => !needle || `${p.brand} ${p.model} ${p.variant}`.toLowerCase().includes(needle),
  );
  return (
    <Modal
      open
      passiveModal
      modalHeading={t('hw.build.pickHeading')}
      onRequestClose={onClose}
      size="sm"
    >
      <div className="ddp-pick">
        <Search
          id="pick-search"
          size="sm"
          labelText={t('hw.catalog.searchLabel')}
          placeholder={t('hw.catalog.searchPlaceholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <ul className="ddp-pick__list">
          {shown.map((p) => (
            <li key={p.id} className="ddp-pick__row">
              <span className="ddp-pick__name">
                <b>
                  {p.brand} {p.model}
                </b>
                <span className="ddp-pick__meta">
                  {t(categoryKey(p.category))}
                  {p.variant ? ` · ${p.variant}` : ''}
                </span>
              </span>
              <Button size="sm" kind="ghost" renderIcon={Add} onClick={() => onPick(p)}>
                {t('hw.build.pickAdd')}
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

interface OverrideRow {
  k: string;
  v: string;
}

/**
 * Création d'une variante tunée (ADR-0030 §3), rattachée à l'officiel `part`. Nom + note libres, et
 * des **surcharges de specs** optionnelles (paires clé/valeur) — la variante hérite du parent et ne
 * porte que ce qui diffère. Le rattachement à l'officiel est garanti ici (le parent est `part`) et
 * revérifié côté API.
 */
function VariantModal({
  part,
  onCreate,
  onCreated,
  onClose,
}: {
  part: CatalogPart;
  onCreate: (input: {
    parentPartId: string;
    label: string;
    specOverrides: CatalogSpecs;
    note?: string;
  }) => Promise<OrgPartVariant>;
  onCreated: (v: OrgPartVariant) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [rows, setRows] = useState<OverrideRow[]>([{ k: '', v: '' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!label.trim()) return;
    setBusy(true);
    setError(null);
    const specOverrides: CatalogSpecs = {};
    for (const r of rows) {
      const key = r.k.trim();
      if (key) specOverrides[key] = r.v.trim();
    }
    try {
      const created = await onCreate({
        parentPartId: part.id,
        label: label.trim(),
        specOverrides,
        note: note.trim() || undefined,
      });
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('hw.variant.createError'));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      modalHeading={t('hw.variant.heading')}
      primaryButtonText={busy ? t('hw.variant.creating') : t('hw.variant.create')}
      secondaryButtonText={t('hw.variant.cancel')}
      primaryButtonDisabled={!label.trim() || busy}
      onRequestSubmit={() => void submit()}
      onSecondarySubmit={onClose}
      onRequestClose={onClose}
      size="sm"
    >
      <p className="ddp-variant__parent">
        {t('hw.variant.for')} :{' '}
        <b>
          {part.brand} {part.model}
        </b>
        {part.variant ? ` · ${part.variant}` : ''}
      </p>
      <div className="ddp-variant__form">
        <TextInput
          id="variant-label"
          labelText={t('hw.variant.labelField')}
          placeholder={t('hw.variant.labelPlaceholder')}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <TextInput
          id="variant-note"
          labelText={t('hw.variant.noteField')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <fieldset className="ddp-variant__overrides">
          <legend>{t('hw.variant.overrides')}</legend>
          {rows.map((r, i) => (
            <div key={i} className="ddp-variant__row">
              <TextInput
                id={`ov-k-${i}`}
                size="sm"
                labelText={t('hw.variant.overrideKey')}
                hideLabel
                placeholder={t('hw.variant.overrideKey')}
                value={r.k}
                onChange={(e) =>
                  setRows((rs) => rs.map((x, j) => (j === i ? { ...x, k: e.target.value } : x)))
                }
              />
              <TextInput
                id={`ov-v-${i}`}
                size="sm"
                labelText={t('hw.variant.overrideValue')}
                hideLabel
                placeholder={t('hw.variant.overrideValue')}
                value={r.v}
                onChange={(e) =>
                  setRows((rs) => rs.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))
                }
              />
              <button
                type="button"
                className="ddp-build__rm"
                aria-label={t('hw.variant.removeOverride')}
                onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs))}
              >
                ✕
              </button>
            </div>
          ))}
          <Button
            size="sm"
            kind="ghost"
            renderIcon={Add}
            onClick={() => setRows((rs) => [...rs, { k: '', v: '' }])}
          >
            {t('hw.variant.addOverride')}
          </Button>
        </fieldset>
        {error && (
          <InlineNotification kind="error" title={t('common.error')} subtitle={error} lowContrast />
        )}
      </div>
    </Modal>
  );
}
